import { Injectable } from '@angular/core';
import { ValidatorFn } from '@angular/forms';

import { ICampaignSponsorshipItem } from '@fmnts/api/next';
import { hasProperty, isNumber } from '@fmnts/core';
import { Decimal } from '@fmnts/core/decimal';
import {
  IValidationService,
  PrepareFieldValidationOptions,
} from '@fmnts/declarative-forms';
import {
  NumberRangeConditionalValidationConfig,
  NumberRangeValidationService,
} from '@fmnts/declarative-forms/common';
import { TranslationService } from '@fmnts/i18n';
import { PaymentInterval } from '@formunauts/shared/campaigning/domain';

import {
  ConditionalValidatorFn,
  FormInputField,
  isFormInputField,
  isFormSelectField,
} from '@fmnts/declarative-forms/core';

import {
  DeclarativeFormFieldKey,
  declarativeFormFieldPath,
} from '@formunauts/shared/declarative-forms/domain';
import { DonationAmountService } from './donation-amount.service';

export const enum ValidatorNames {
  DonationAmount = 'donationAmount',
}

export interface DonationAmountValidationConfig extends NumberRangeConditionalValidationConfig {
  name: ValidatorNames.DonationAmount;
}

type ConditionalAmountValidators =
  DonationAmountValidationConfig['conditionalArgs'];

type AmountByInterval = (
  amount: number,
  interval: PaymentInterval.PaymentInterval,
) => number;

/**
 * Type guard to check if value is an object and
 * has a `min_amount` of type `number`
 */
const hasMinAmount = hasProperty('min_amount', isNumber);

/**
 * Validation service that builds validator functions and
 * validation configurations for functionality related
 * to the donation amount.
 */
@Injectable({
  providedIn: 'root',
})
export class DonationAmountValidationService implements IValidationService {
  constructor(
    private donationAmountService: DonationAmountService,
    private numberRangeValidationService: NumberRangeValidationService,
    private translationService: TranslationService,
  ) {}

  public buildValidator(
    validator: DonationAmountValidationConfig,
  ): ValidatorFn[] {
    switch (validator.name) {
      // Currently, this validator is only created on the frontend side
      case ValidatorNames.DonationAmount:
        return [...this.buildDonationAmountValidation(validator)];
      default:
        return [];
    }
  }

  public buildDonationAmountValidation(
    opts: DonationAmountValidationConfig,
  ): ConditionalValidatorFn[] {
    return this.numberRangeValidationService.buildNumberRangeConditionalValidations(
      opts,
    );
  }

  public prepareField(
    options: PrepareFieldValidationOptions,
  ): FormInputField | undefined {
    const { field } = options;
    if (!isFormInputField(field) || !field.widgetOptions.validators) {
      return undefined;
    }
    // For the donation amount field it might be needed to
    // adapt the validation configuration
    if (DeclarativeFormFieldKey.DonationAmount === field.key) {
      return {
        ...field,
        widgetOptions: {
          ...field.widgetOptions,
          validators: this.buildDonationAmountValidatorConfig(options) ?? [],
        },
      };
    }

    return field;
  }

  /**
   * Builds the validation configuration for the donation amount field.
   *
   * @param options Passed build options for validator
   *
   * @returns
   * Validation configuration
   */
  private buildDonationAmountValidatorConfig(
    options: PrepareFieldValidationOptions,
  ) {
    const { field } = options;
    if (!isFormInputField(field)) {
      return [];
    }
    const showPerInterval = field.attrs?.['show_per_interval'] ?? false;
    const conditionalArgs: ConditionalAmountValidators = [
      ...(this.buildAmountValidatorsFromSponsorship(options, showPerInterval) ??
        []),
      ...(this.buildAmountValidatorsFromInterval(options, showPerInterval) ??
        []),
    ];

    // Nothing changed, return as is
    if (conditionalArgs.length === 0) {
      return field.widgetOptions.validators;
    }

    const numberRangeValidator = field.widgetOptions.validators.find(
      (v) => v.name === 'numberRange',
    );
    const donationAmountValidator: DonationAmountValidationConfig = {
      name: ValidatorNames.DonationAmount,
      conditionalArgs,
      args: {},
      messages: {
        interval_min: this.translationService.getTranslation(
          'field_error_min_donation_amount_interval',
        ),
        sponsorship_min: this.translationService.getTranslation(
          'field_error_min_donation_amount_sponsorship',
        ),
      },
    };

    // If there is no number range validator, we still "inject" the created config
    if (!numberRangeValidator) {
      return [...field.widgetOptions.validators, donationAmountValidator];
    }

    // Prepare validator configurations for field
    return field.widgetOptions.validators.map((v) =>
      // replace the numberRange validator
      v.name !== 'numberRange'
        ? v
        : {
            ...donationAmountValidator,
            args: v.args,
          },
    );
  }

  /**
   * Builds the validation configuration for the donation amount
   * that depends on the chosen interval.
   *
   * @param options Passed build options for validator
   * @param showPerInterval Field will show amounts per interval
   *
   * @returns
   * Validation configuration args
   */
  private buildAmountValidatorsFromInterval(
    options: PrepareFieldValidationOptions,
    showPerInterval: boolean,
  ): ConditionalAmountValidators {
    const { declaration } = options;
    const {
      directDebitInterval: intervalFieldKey,
      donationAmountPerInterval: donationAmountPerIntervalFieldKey,
    } = declarativeFormFieldPath;

    const intervalField = declaration.get(intervalFieldKey);
    if (!intervalField || !isFormSelectField(intervalField)) {
      // Nothing to create interval related validations from
      return [];
    }

    // We prepare a projection function to convert an amount to a certain
    // interval
    let projectAmountByInterval: AmountByInterval = (amount) => amount;

    if (showPerInterval !== true) {
      // If the field won't show amounts per interval, we need to interpret
      // the items amounts based on the campaigns "calculation_base"
      const calculationBase = declaration.get(donationAmountPerIntervalFieldKey)
        ?.attrs?.['calculation_base'];

      if (!calculationBase) {
        // If there is no calculation base, skip creating validators
        // This is the case for e.g. self-checkout
        // TODO: Maybe backend should always send a calculation_base
        return [];
      }

      // Provide a new projection that does the required transformation
      // from the amount by interval to the calculation base
      projectAmountByInterval = (amount, interval) =>
        this.donationAmountService
          .toBase(new Decimal(amount), interval, calculationBase)
          .toDecimalPlaces(2, Decimal.ROUND_HALF_CEIL)
          .toNumber();
    }

    if (!isFormSelectField(intervalField)) {
      throw new Error();
    }

    return intervalField.widgetOptions.items
      .filter((item) => hasMinAmount(item))
      .map((item) => ({
        condition: { key: intervalFieldKey.join('.'), equals: item.value },
        messageRefs: {
          conditionalMin: 'interval_min',
        },
        min: projectAmountByInterval(item['min_amount'], item.value),
      }));
  }

  /**
   * Builds the validation configuration for the donation amount
   * that depends on the chosen sponsorship.
   *
   * @param options Passed build options for validator
   * @param showPerInterval Field will show amounts per interval
   *
   * @returns
   * Validation configuration args
   */
  private buildAmountValidatorsFromSponsorship(
    options: PrepareFieldValidationOptions,
    showPerInterval: boolean,
  ): ConditionalAmountValidators {
    const { declaration } = options;
    const sponsorshipField = declaration.get(
      declarativeFormFieldPath.sponsorship,
    );
    if (!sponsorshipField || !isFormSelectField(sponsorshipField)) {
      // Nothing to create sponsorship related validations from
      return [];
    }

    const items = sponsorshipField.widgetOptions
      .items as ICampaignSponsorshipItem[];
    return items.flatMap(
      (sponsorship) =>
        this.buildSponsorshipItemValidation(sponsorship, showPerInterval) ?? [],
    );
  }

  /**
   * Builds validation configuration for the given `sponsorship` item
   * based on the forms base interval.
   *
   * @param sponsorship Sponsorship item
   * @param showPerInterval Base interval that the form uses to describe amounts
   *
   * @returns
   * Array of validation configurations
   */
  private buildSponsorshipItemValidation(
    sponsorship: ICampaignSponsorshipItem,
    showPerInterval: boolean,
  ): ConditionalAmountValidators {
    const baseMin = hasMinAmount(sponsorship)
      ? new Decimal(sponsorship.min_amount)
      : null;

    // Nothing to build for this particular sponsorship item
    if (!baseMin) {
      return [];
    }

    const sponsorshipCondition = {
      key: declarativeFormFieldPath.sponsorship.join('.'),
      equals: sponsorship.value,
    };

    // When the form doesn't use the "per interval" setting
    // we can create a single validation for the sponsorship
    if (!showPerInterval) {
      return [
        {
          condition: sponsorshipCondition,
          messageRefs: {
            conditionalMin: 'sponsorship_min',
          },
          min: baseMin ? sponsorship.min_amount : undefined,
        },
      ];
    }

    // For the "per interval" setting we must create a validation
    // for each (recurring) interval. For "one-off" interval we
    // won't provide any validation
    const intervalPathKey =
      declarativeFormFieldPath.directDebitInterval.join('.');
    return PaymentInterval.recurringIntervals.map((interval) => ({
      condition: {
        and: [sponsorshipCondition, { key: intervalPathKey, equals: interval }],
      },
      min: baseMin
        ? this.donationAmountService
            .toInterval(baseMin, sponsorship.calculation_base, interval)
            .toDecimalPlaces(2, Decimal.ROUND_HALF_CEIL)
            .toNumber()
        : undefined,
      messageRefs: {
        conditionalMin: 'sponsorship_min',
      },
    }));
  }
}
